import config from '../../../config'
import { authHeader, customFetch, handleResponse, commonRequestOptions } from '../../../helpers'
import { appName } from '../appConfig'

const base = `${appName}/generation`

export const generationService = {
  getUsers,
  getHistory,
  generate,
  claim,
  breakLoose,
  eject,
  updateDates,
  softDelete,
  restore,
  getStats,
}

function getUsers(signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/users`, opts, signal).then(handleResponse)
}

// deleted: boolean, start/end: 'YYYY-MM-DD' wall-clock date strings, page/pageSize: number.
// Sent through verbatim, NOT via new Date(...).toISOString() — the round columns are wall
// clock, so converting to UTC first shifted the filter bound by the viewer's offset and lost
// or gained a day's rounds at each edge. Postgres casts a bare date on the typed base table.
function getHistory({ deleted = false, start, end, page = 1, pageSize = 20 } = {}, signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  const params = new URLSearchParams({ deleted: deleted.toString(), page, pageSize })
  if (start) params.set('start', start)
  if (end)   params.set('end', end)
  return customFetch(`${config.api.url}/${base}?${params.toString()}`, opts, signal).then(handleResponse)
}

// replaceGenerationId: pass the round being rerolled to replace it in place
// (a redo) instead of creating a separate permanent history entry.
// roundEndTimestamp: optional planned end date for the round (YYYY-MM-DD or
// any ISO 8601 string), null for open-ended.
function generate({ excludedUserIds = [], forcedMegaBuddyUserId = null, replaceGenerationId = null, roundEndTimestamp = null } = {}, signal) {
  const opts = {
    method:  'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify({ excludedUserIds, forcedMegaBuddyUserId, replaceGenerationId, roundEndTimestamp }),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}`, opts, signal).then(handleResponse)
}

function claim(unclaimedUserId, signal) {
  const opts = { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ unclaimedUserId }), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/claim`, opts, signal).then(handleResponse)
}

function breakLoose(unclaimedUserId, signal) {
  const opts = { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ unclaimedUserId }), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/break_loose`, opts, signal).then(handleResponse)
}

// Ejects the specific pairing, not a person — a mega buddy has two separate
// pairings (one per partner), and ejecting one must leave the other intact.
function eject(generationPairId, signal) {
  const opts = { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ generationPairId }), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/eject`, opts, signal).then(handleResponse)
}

// generationTimestamp/roundEndTimestamp: yyyy-mm-dd strings (or any ISO 8601
// string); omit a key to leave that date untouched, or pass
// roundEndTimestamp: null to clear a previously-set end date.
function updateDates(generationId, { generationTimestamp, roundEndTimestamp } = {}, signal) {
  const body = {}
  if (generationTimestamp !== undefined) body.generationTimestamp = generationTimestamp
  if (roundEndTimestamp !== undefined) body.roundEndTimestamp = roundEndTimestamp
  const opts = { method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(body), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/${generationId}/dates`, opts, signal).then(handleResponse)
}

// Pass either { ids: [...] } or { all: true }
function softDelete(payload, signal) {
  const opts = { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/soft_delete`, opts, signal).then(handleResponse)
}

function restore(ids, signal) {
  const opts = { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/restore`, opts, signal).then(handleResponse)
}

// includeUserIds/excludeUserIds: array of user ids, or undefined/empty for no filter
function getStats({ includeUserIds, excludeUserIds } = {}, signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  const params = new URLSearchParams()
  if (includeUserIds?.length) params.set('includeUserIds', includeUserIds.join(','))
  if (excludeUserIds?.length) params.set('excludeUserIds', excludeUserIds.join(','))
  const query = params.toString()
  return customFetch(`${config.api.url}/${base}/stats${query ? `?${query}` : ''}`, opts, signal).then(handleResponse)
}
